import { useState, useRef, useLayoutEffect } from 'react';

import { Dismiss12Filled } from '@fluentui/react-icons';

import type { SelectOption } from '@avodah-inc/types';
import { cn } from '@avodah-inc/ui/utils';

import { capitalize } from '../../../lib/helpers';
import { ScrollArea } from '../scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '../tooltip';
import { Typography } from '../typography';
import { UnstyledButton } from '../unstyled-button';

export type PillProps = {
  label: string;
  value: string;
  onClick?: () => void;
  clearable?: boolean;
  dataTestId?: string;
};

export const Pill = ({
  label,
  value,
  onClick,
  clearable = true,
  dataTestId,
}: PillProps) => {
  return (
    <div
      data-testid={dataTestId}
      className={cn(
        'border-primary-300 flex w-fit items-center justify-between gap-1 rounded-full border-[1px] pl-2 pr-1',
        {
          'px-2': !clearable,
        },
      )}
    >
      <Typography variant="body4" className="text-primary-500">
        {label}
      </Typography>
      {clearable && (
        <UnstyledButton
          key={value}
          onPress={() => {
            onClick?.();
          }}
        >
          <Dismiss12Filled className="text-primary-500" />
        </UnstyledButton>
      )}
    </div>
  );
};

type PillListProps<T extends SelectOption> = {
  list: T[];
  onClick: (item: T) => void;
  maxRows?: number;
  dataTestId?: string;
};

const createPillElement = (label: string, isMorePill = false) => {
  const pillDiv = document.createElement('div');
  pillDiv.className =
    'border-primary-300 flex w-fit items-center justify-between gap-1 rounded-full border-[1px] pl-2 pr-1';
  if (isMorePill) {
    pillDiv.className =
      'border-primary-300 flex w-fit items-center justify-between gap-1 rounded-full border-[1px] px-2';
    pillDiv.setAttribute('data-more-pill', 'true');
    pillDiv.innerHTML = '<span>+99</span>';
  } else {
    pillDiv.innerHTML = `<span>${capitalize(label)}</span>`;
  }
  return pillDiv;
};

const calculateRowWidth = (
  pillElement: HTMLElement,
  currentRowWidth: number,
  containerWidth: number,
) => {
  const pillWidth = Math.ceil(pillElement.getBoundingClientRect().width);
  const withGap =
    pillWidth + (currentRowWidth + pillWidth < containerWidth ? 10 : 0);
  return { pillWidth, withGap };
};

const getMorePillWidth = (hiddenContainer: HTMLDivElement) => {
  const morePillElement = hiddenContainer.querySelector(
    '[data-more-pill="true"]',
  );
  return morePillElement
    ? Math.ceil(morePillElement.getBoundingClientRect().width)
    : 60;
};

const shouldShowAllPills = (list: SelectOption[], containerWidth: number) => {
  return list.length <= 2 && containerWidth >= 200;
};

const getPillElements = (hiddenContainer: HTMLDivElement) => {
  return Array.from(hiddenContainer.children).filter(
    (el) => !el.hasAttribute('data-more-pill'),
  );
};

const shouldAddToNewRow = (
  currentRowWidth: number,
  withGap: number,
  containerWidth: number,
) => {
  return currentRowWidth + withGap > containerWidth;
};

const shouldShowMorePill = (
  maxRows: number | undefined,
  currentRow: number,
  i: number,
  listLength: number,
  remainingSpace: number,
  morePillWidth: number,
) => {
  if (!maxRows) return false;
  return (
    currentRow === maxRows &&
    i < listLength - 1 &&
    remainingSpace < morePillWidth + 8
  );
};

const processPillVisibility = <T extends SelectOption>(
  list: T[],
  pillElements: Element[],
  containerWidth: number,
  maxRows: number | undefined,
  morePillWidth: number,
) => {
  let currentRow = 1;
  let currentRowWidth = 0;
  const visible: T[] = [];
  let needsMorePill = false;

  for (let i = 0; i < list.length && i < pillElements.length; i++) {
    const pillElement = pillElements[i] as HTMLElement;
    const item = list[i];
    if (!item) continue;

    const { pillWidth, withGap } = calculateRowWidth(
      pillElement,
      currentRowWidth,
      containerWidth,
    );

    if (shouldAddToNewRow(currentRowWidth, withGap, containerWidth)) {
      currentRow++;
      if (maxRows && currentRow > maxRows) {
        needsMorePill = true;
        break;
      }
      currentRowWidth = pillWidth;
    } else {
      currentRowWidth += withGap;
    }

    const remainingSpace = containerWidth - currentRowWidth;
    if (
      shouldShowMorePill(
        maxRows,
        currentRow,
        i,
        list.length,
        remainingSpace,
        morePillWidth,
      )
    ) {
      needsMorePill = true;
      break;
    }

    visible.push(item);
  }

  return { visible, needsMorePill };
};

const calculatePillVisibility = <T extends SelectOption>(
  containerWidth: number,
  list: T[],
  hiddenContainer: HTMLDivElement,
  maxRows?: number,
) => {
  const morePillWidth = getMorePillWidth(hiddenContainer);

  if (shouldShowAllPills(list, containerWidth)) {
    return { visiblePills: list, remainingCount: 0 };
  }

  const pillElements = getPillElements(hiddenContainer);
  const { visible, needsMorePill } = processPillVisibility(
    list,
    pillElements,
    containerWidth,
    maxRows,
    morePillWidth,
  );

  return {
    visiblePills: needsMorePill ? visible : list,
    remainingCount: needsMorePill
      ? Math.max(0, list.length - visible.length)
      : 0,
  };
};

export const PillList = <T extends SelectOption>({
  list,
  onClick,
  maxRows,
  dataTestId = 'pill-list',
}: PillListProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hiddenContainerRef = useRef<HTMLDivElement>(null);
  const [visiblePills, setVisiblePills] = useState<T[]>(list);
  const [remainingCount, setRemainingCount] = useState<number>(0);
  const [isReady, setIsReady] = useState(false);
  const prevListRef = useRef<T[]>(list);

  const handlePillClick = (item: T) => {
    onClick(item);
  };

  useLayoutEffect(() => {
    if (!maxRows) {
      setVisiblePills(list);
      setIsReady(true);
      return;
    }

    if (!list.length) {
      setVisiblePills([]);
      setRemainingCount(0);
      setIsReady(true);
      return;
    }

    if (prevListRef.current !== list) {
      prevListRef.current = list;
    }

    const updatePillVisibility = () => {
      if (!containerRef.current || !hiddenContainerRef.current) return;

      hiddenContainerRef.current.innerHTML = '';
      list.forEach((item) => {
        hiddenContainerRef.current?.appendChild(createPillElement(item.label));
      });
      hiddenContainerRef.current.appendChild(createPillElement('', true));

      const containerWidth = containerRef.current.getBoundingClientRect().width;
      const {
        visiblePills: newVisiblePills,
        remainingCount: newRemainingCount,
      } = calculatePillVisibility(
        containerWidth,
        list,
        hiddenContainerRef.current,
        maxRows,
      );

      setVisiblePills(newVisiblePills);
      setRemainingCount(newRemainingCount);
      setIsReady(true);
    };

    updatePillVisibility();

    const handleResize = () => {
      const timeoutId = setTimeout(updatePillVisibility, 100);
      return () => {
        clearTimeout(timeoutId);
      };
    };

    const cleanup = handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      cleanup();
      window.removeEventListener('resize', handleResize);
    };
  }, [list, maxRows]);

  if (!maxRows) {
    return (
      <div
        className="flex flex-wrap gap-2 transition-all duration-300 ease-in-out"
        ref={containerRef}
        data-testid={`${dataTestId}-container`}
      >
        {list.map((item) => (
          <Pill
            key={item.value}
            label={capitalize(item.label)}
            value={item.value}
            onClick={() => {
              handlePillClick(item);
            }}
            dataTestId={`${dataTestId}-${item.value}`}
          />
        ))}
      </div>
    );
  }

  return (
    <>
      <div
        ref={hiddenContainerRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          width: containerRef.current?.getBoundingClientRect().width || '100%',
          left: '-9999px',
          top: '-9999px',
        }}
      />

      <div
        className="flex flex-wrap gap-2 transition-all duration-300 ease-in-out"
        ref={containerRef}
        style={{
          opacity: isReady ? 1 : 0,
          transition: 'opacity 150ms ease-in-out',
        }}
        data-testid={`${dataTestId}-container`}
      >
        {visiblePills.map((item) => (
          <Pill
            key={item.value}
            label={capitalize(item.label)}
            value={item.value}
            onClick={() => {
              handlePillClick(item);
            }}
            dataTestId={`${dataTestId}-${item.value}`}
          />
        ))}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger>
              <Pill
                clearable={false}
                label={`+${remainingCount}`}
                value=""
                dataTestId={`${dataTestId}-more`}
              />
            </TooltipTrigger>
            <TooltipContent
              className="w-[376px] bg-white"
              side="bottom"
              align="start"
            >
              <ScrollArea
                display="block"
                className={cn('h-fit w-full', {
                  'h-[300px]': list.length > 19,
                })}
              >
                <div className="flex flex-wrap gap-2">
                  {list.map((item) => (
                    <Pill
                      key={item.value}
                      label={capitalize(item.label)}
                      value={item.value}
                      onClick={() => {
                        handlePillClick(item);
                      }}
                      dataTestId={`${dataTestId}-${item.value}`}
                    />
                  ))}
                </div>
              </ScrollArea>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </>
  );
};
